"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SqlGenerator = void 0;
const ast_1 = require("../ast");
const utils_1 = require("../utils");
// Generic AST-to-SQL generator. All dialect-specific behaviour lives in
// the DialectConfig passed to the constructor — there are no subclasses.
// Fields the config doesn't set fall back to ANSI-standard defaults.
class SqlGenerator {
    constructor(options, dialect) {
        this.options = options;
        this.dialect = dialect;
    }
    // Public entry point. Dispatches to the node-specific generator, then
    // hands aggregate output to the caller-supplied `renderAggregate` callback
    // (if any) so the caller decides how to embed the aggregate in their SQL
    // context — e.g. window-wrap for post-aggregation SELECTs, pass through
    // for GROUP BY contexts. Every recursive call from child arms goes back
    // through `generate()`, so the hook is applied once per aggregate node
    // anywhere in the tree.
    generate(node) {
        const sql = this.generateNode(node);
        if ((0, ast_1.isAggregateCall)(node) && this.options.renderAggregate) {
            return this.options.renderAggregate(sql);
        }
        return sql;
    }
    generateNode(node) {
        switch (node.type) {
            case 'BinaryOp':
                return this.generateBinaryOp(node);
            case 'UnaryOp':
                return this.generateUnaryOp(node);
            case 'If':
                return this.generateIf(node);
            case 'ConditionalAggregate':
                return this.generateConditionalAggregate(node);
            case 'CountIf':
                return this.generateCountIf(node);
            case 'ZeroArgFn':
                return this.generateZeroArgFn(node);
            case 'SingleArgFn':
                return this.generateSingleArgFn(node);
            case 'OneOrTwoArgFn':
                return this.generateOneOrTwoArgFn(node);
            case 'TwoArgFn':
                return this.generateTwoArgFn(node);
            case 'ThreeArgFn':
                return this.generateThreeArgFn(node);
            case 'ZeroOrOneArgFn':
                return this.generateZeroOrOneArgFn(node);
            case 'VariadicFn':
                return this.generateVariadicFn(node);
            case 'WindowFn':
                return this.generateWindowFn(node);
            case 'MovingWindowFn':
                return this.generateMovingWindowFn(node);
            case 'DateFn':
                return this.generateDateFn(node);
            case 'ColumnRef':
                return this.generateColumnRef(node);
            case 'NumberLiteral':
                return this.generateNumberLiteral(node);
            case 'StringLiteral':
                return this.generateStringLiteral(node);
            case 'BooleanLiteral':
                return this.generateBooleanLiteral(node);
            case 'Comparison':
                return this.generateComparison(node);
            case 'Logical':
                return this.generateLogical(node);
            default:
                return (0, utils_1.assertUnreachable)(node, `Unknown node type: ${node.type}`);
        }
    }
    generateBinaryOp(node) {
        const left = this.generate(node.left);
        const right = this.generate(node.right);
        switch (node.op) {
            case '+':
            case '-':
            case '*':
                return `(${left} ${node.op} ${right})`;
            case '/':
                return `(${left} / NULLIF(${right}, 0))`;
            case '^':
                return `POWER(${left}, ${right})`;
            case '%':
                return this.generateModulo(left, right);
            default:
                return (0, utils_1.assertUnreachable)(node.op, `Unknown operator: ${node.op}`);
        }
    }
    generateUnaryOp(node) {
        const operand = this.generate(node.operand);
        switch (node.op) {
            case '-':
                return `(-(${operand}))`;
            case 'NOT':
                return `(NOT ${operand})`;
            default:
                return (0, utils_1.assertUnreachable)(node.op, `Unknown unary op: ${node.op}`);
        }
    }
    generateIf(node) {
        const condition = this.generate(node.condition);
        const then = this.generate(node.then);
        const else_ = node.else ? this.generate(node.else) : 'NULL';
        return `CASE WHEN ${condition} THEN ${then} ELSE ${else_} END`;
    }
    generateConditionalAggregate(node) {
        const value = this.generate(node.value);
        const condition = this.generate(node.condition);
        switch (node.name) {
            case 'SUMIF':
                return `SUM(CASE WHEN ${condition} THEN ${value} END)`;
            case 'AVERAGEIF':
                return `AVG(CASE WHEN ${condition} THEN ${value} END)`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown conditional aggregate: ${node.name}`);
        }
    }
    generateCountIf(node) {
        const condition = this.generate(node.condition);
        return `COUNT(CASE WHEN ${condition} THEN 1 END)`;
    }
    generateZeroArgFn(node) {
        switch (node.name) {
            case 'TODAY':
                return this.generateToday();
            case 'NOW':
                return this.generateNow();
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown zero-arg function: ${node.name}`);
        }
    }
    generateSingleArgFn(node) {
        const arg = this.generate(node.arg);
        switch (node.name) {
            case 'ABS':
                return `ABS(${arg})`;
            case 'CEIL':
            case 'CEILING':
                return `CEIL(${arg})`;
            case 'FLOOR':
                return `FLOOR(${arg})`;
            case 'LEN':
            case 'LENGTH':
                return this.generateLength(arg);
            case 'TRIM':
                return `TRIM(${arg})`;
            case 'LOWER':
                return `LOWER(${arg})`;
            case 'UPPER':
                return `UPPER(${arg})`;
            case 'YEAR':
                return this.generateExtract('YEAR', arg);
            case 'MONTH':
                return this.generateExtract('MONTH', arg);
            case 'DAY':
                return this.generateExtract('DAY', arg);
            case 'LAST_DAY':
                return this.generateLastDay(arg);
            case 'ISNULL':
                return `(${arg} IS NULL)`;
            case 'SUM':
                return `SUM(${arg})`;
            case 'AVERAGE':
            case 'AVG':
                return this.generateAvg(arg);
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown single-arg function: ${node.name}`);
        }
    }
    generateOneOrTwoArgFn(node) {
        const args = node.args.map((a) => this.generate(a));
        switch (node.name) {
            case 'ROUND':
                if (this.dialect.generateRound) {
                    return this.dialect.generateRound(args[0], args[1]);
                }
                return `ROUND(${args[0]}${args[1] !== undefined ? `, ${args[1]}` : ''})`;
            case 'MIN':
                return args.length === 1
                    ? `MIN(${args[0]})`
                    : `LEAST(${args.join(', ')})`;
            case 'MAX':
                return args.length === 1
                    ? `MAX(${args[0]})`
                    : `GREATEST(${args.join(', ')})`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown one-or-two-arg function: ${node.name}`);
        }
    }
    generateTwoArgFn(node) {
        const [a, b] = node.args.map((x) => this.generate(x));
        switch (node.name) {
            case 'LEFT':
                return this.generateLeft(a, b);
            case 'RIGHT':
                return this.generateRight(a, b);
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown two-arg function: ${node.name}`);
        }
    }
    generateLeft(text, count) {
        return (this.dialect.generateLeft?.(text, count) ??
            `LEFT(${text}, ${count})`);
    }
    generateRight(text, count) {
        return (this.dialect.generateRight?.(text, count) ??
            `RIGHT(${text}, ${count})`);
    }
    generateThreeArgFn(node) {
        const [a, b, c] = node.args.map((x) => this.generate(x));
        switch (node.name) {
            case 'REPLACE':
                return `REPLACE(${a}, ${b}, ${c})`;
            case 'SUBSTRING':
                return this.generateSubstring(a, b, c);
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown three-arg function: ${node.name}`);
        }
    }
    generateSubstring(text, start, length) {
        return (this.dialect.generateSubstring?.(text, start, length) ??
            `SUBSTRING(${text}, ${start}, ${length})`);
    }
    generateZeroOrOneArgFn(node) {
        switch (node.name) {
            case 'COUNT':
                return node.arg
                    ? `COUNT(${this.generate(node.arg)})`
                    : 'COUNT(*)';
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown zero-or-one-arg function: ${node.name}`);
        }
    }
    generateVariadicFn(node) {
        const args = node.args.map((a) => this.generate(a));
        switch (node.name) {
            case 'CONCAT':
                return this.generateConcat(args);
            case 'COALESCE':
                return `COALESCE(${args.join(', ')})`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown variadic function: ${node.name}`);
        }
    }
    generateWindowFn(node) {
        const args = node.args.map((a) => this.generate(a));
        switch (node.name) {
            case 'ROW_NUMBER':
                return this.generateWindowFunction('ROW_NUMBER', [], node);
            case 'RANK':
                return this.generateWindowFunction('RANK', [], node);
            case 'DENSE_RANK':
                return this.generateWindowFunction('DENSE_RANK', [], node);
            case 'RUNNING_TOTAL':
                return this.generateWindowFunction('SUM', [args[0]], node, 'ROWS UNBOUNDED PRECEDING');
            case 'NTILE':
                return this.generateWindowFunction('NTILE', [args[0]], node);
            // FIRST_VALUE and LAST_VALUE both get an explicit
            // `UNBOUNDED PRECEDING..UNBOUNDED FOLLOWING` frame. For
            // LAST_VALUE it's required — the ANSI default frame ends at
            // CURRENT ROW, so without this LAST_VALUE returns the current
            // row (not the last). For FIRST_VALUE it's a semantic no-op
            // (the first row of the partition is the first row regardless
            // of the frame's upper bound) but required on Redshift, which
            // rejects aggregate-style windows with ORDER BY and no frame.
            case 'FIRST':
            case 'LAST': {
                const sqlFn = node.name === 'FIRST' ? 'FIRST_VALUE' : 'LAST_VALUE';
                return this.generateWindowFunction(sqlFn, [args[0]], node, 'ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING');
            }
            case 'LAG':
            case 'LEAD':
                return this.dispatchLagLead(node.name, args, node);
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown window function: ${node.name}`);
        }
    }
    generateMovingWindowFn(node) {
        const arg = this.generate(node.arg);
        const frame = `ROWS BETWEEN ${node.preceding} PRECEDING AND CURRENT ROW`;
        switch (node.name) {
            case 'MOVING_SUM':
                return this.generateWindowFunction('SUM', [arg], node, frame);
            case 'MOVING_AVG':
                // Route through generateAvg so Redshift's DOUBLE PRECISION cast applies.
                return this.appendOverClause(this.generateAvg(arg), node, frame);
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown moving-window function: ${node.name}`);
        }
    }
    generateColumnRef(node) {
        const column = this.options.columns[node.name];
        if (!column) {
            throw new Error(`Unknown column reference: ${node.name}. Available columns: ${Object.keys(this.options.columns).join(', ')}`);
        }
        return this.quoteIdentifier(column);
    }
    generateNumberLiteral(node) {
        return String(node.value);
    }
    generateBooleanLiteral(node) {
        return node.value ? 'TRUE' : 'FALSE';
    }
    generateComparison(node) {
        const left = this.generate(node.left);
        const right = this.generate(node.right);
        return `(${left} ${node.op} ${right})`;
    }
    generateLogical(node) {
        const left = this.generate(node.left);
        const right = this.generate(node.right);
        return `(${left} ${node.op} ${right})`;
    }
    // --- Dialect-configurable emission ---
    // These methods consult the DialectConfig and fall back to ANSI-standard
    // defaults when the dialect doesn't override.
    quoteIdentifier(name) {
        return this.dialect.quoteIdentifier(name);
    }
    generateStringLiteral(node) {
        if (this.dialect.generateStringLiteral) {
            return this.dialect.generateStringLiteral(node);
        }
        const escaped = node.value.replace(/'/g, "''");
        return `'${escaped}'`;
    }
    generateModulo(left, right) {
        return (this.dialect.generateModulo?.(left, right) ??
            `MOD(${left}, ${right})`);
    }
    // LAG / LEAD dispatch: hand off to the dialect's `generateLagLead` if
    // present, otherwise use the ANSI-compatible default (bare LAG/LEAD
    // with default frame, variadic args). The dialect callback receives
    // an `emitWindow` bound to this node's window clause so dialects don't
    // have to own PARTITION BY / ORDER BY plumbing.
    dispatchLagLead(sqlFunc, args, node) {
        const emitWindow = (fn, funcArgs, frameClause) => this.generateWindowFunction(fn, funcArgs, node, frameClause);
        if (this.dialect.generateLagLead) {
            return this.dialect.generateLagLead({
                sqlFunc,
                args,
                emitWindow,
            });
        }
        return emitWindow(sqlFunc, args);
    }
    // --- ANSI defaults shared across all dialects today ---
    // Promote to DialectConfig fields when a real dialect first diverges.
    generateConcat(args) {
        if (this.dialect.generateConcat) {
            return this.dialect.generateConcat(args);
        }
        return `CONCAT(${args.join(', ')})`;
    }
    generateAvg(arg) {
        if (this.dialect.generateAvg) {
            return this.dialect.generateAvg(arg);
        }
        return `AVG(${arg})`;
    }
    generateLength(expr) {
        return `LENGTH(${expr})`;
    }
    generateToday() {
        return 'CURRENT_DATE';
    }
    generateNow() {
        return 'CURRENT_TIMESTAMP';
    }
    generateExtract(part, expr) {
        return `EXTRACT(${part} FROM ${expr})`;
    }
    generateLastDay(arg) {
        return this.dialect.generateLastDay?.(arg) ?? `LAST_DAY(${arg})`;
    }
    generateDateFn(node) {
        switch (node.name) {
            case 'DATE_TRUNC':
                return this.generateDateTrunc(node.unit, this.generate(node.args[0]));
            case 'DATE_ADD':
                return this.generateDateAdd(node.unit, this.generate(node.args[0]), this.generate(node.args[1]));
            case 'DATE_DIFF':
                return this.generateDateDiff(node.unit, this.generate(node.args[0]), this.generate(node.args[1]));
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown date function: ${node.name}`);
        }
    }
    generateDateTrunc(unit, arg) {
        const weekStartDay = this.options.weekStartDay ?? 0;
        return (this.dialect.generateDateTrunc?.(unit, arg, weekStartDay) ??
            this.defaultDateTrunc(unit, arg, weekStartDay));
    }
    // ANSI-style `DATE_TRUNC('unit', d)` with INTERVAL-based week-start offset
    // for non-Monday starts. Matches
    // `packages/common/src/utils/timeFrames.ts` `postgresConfig`. Used as the
    // default for Postgres, Redshift, Snowflake, and DuckDB. BigQuery /
    // Databricks / ClickHouse override entirely.
    defaultDateTrunc(unit, arg, weekStartDay) {
        if (unit === 'week' && weekStartDay !== 0) {
            const diff = `${weekStartDay} days`;
            return `(DATE_TRUNC('week', (${arg} - INTERVAL '${diff}')) + INTERVAL '${diff}')`;
        }
        return `DATE_TRUNC('${unit}', ${arg})`;
    }
    generateDateAdd(unit, date, n) {
        return (this.dialect.generateDateAdd?.(unit, date, n) ??
            this.defaultDateAdd(unit, date, n));
    }
    // Postgres/Redshift/DuckDB: `(d + (n) * INTERVAL '1 <unit>')`. Works
    // across all five units and propagates NULL naturally. BigQuery,
    // Snowflake, Databricks, ClickHouse override with their native forms.
    // Postgres has no `quarter` interval unit (only day/week/month/year);
    // emit `(n) * 3 months` instead so the path stays valid there.
    defaultDateAdd(unit, date, n) {
        if (unit === 'quarter') {
            return `(${date} + (${n}) * INTERVAL '3 months')`;
        }
        return `(${date} + (${n}) * INTERVAL '1 ${unit}')`;
    }
    generateDateDiff(unit, start, end) {
        const weekStartDay = this.options.weekStartDay ?? 0;
        // Non-Monday week-diff composes via day-diff between week-truncated
        // endpoints. The week truncation is already per-dialect (respects
        // weekStartDay), so every dialect gets correct non-Monday behaviour
        // without re-implementing the offset dance in each override.
        if (unit === 'week' && weekStartDay !== 0) {
            const startTrunc = this.generateDateTrunc('week', start);
            const endTrunc = this.generateDateTrunc('week', end);
            return `(${this.invokeDateDiff('day', startTrunc, endTrunc)} / 7)`;
        }
        return this.invokeDateDiff(unit, start, end);
    }
    invokeDateDiff(unit, start, end) {
        return (this.dialect.generateDateDiff?.(unit, start, end) ??
            this.defaultDateDiff(unit, start, end));
    }
    // Postgres emulation (Postgres has no native DATEDIFF). Every other
    // dialect overrides with its native form. Semantics: whole-unit calendar-
    // boundary count, positive when `end > start` — matches BigQuery /
    // Snowflake / DuckDB / Databricks / ClickHouse / Redshift DATEDIFF.
    // Week/quarter/month/year fall out of DATE_PART subtractions; day is the
    // native integer `(date - date)` operator. Non-Monday week handling is
    // done at the `generateDateDiff` level so this function always sees
    // Monday-aligned weeks.
    defaultDateDiff(unit, start, end) {
        switch (unit) {
            case 'day':
                return `((${end})::date - (${start})::date)`;
            case 'week':
                return `((DATE_TRUNC('week', ${end})::date - DATE_TRUNC('week', ${start})::date) / 7)`;
            case 'month':
                return `((DATE_PART('year', ${end}) - DATE_PART('year', ${start})) * 12 + (DATE_PART('month', ${end}) - DATE_PART('month', ${start})))::int`;
            case 'quarter':
                return `((DATE_PART('year', ${end}) - DATE_PART('year', ${start})) * 4 + (DATE_PART('quarter', ${end}) - DATE_PART('quarter', ${start})))::int`;
            case 'year':
                return `(DATE_PART('year', ${end}) - DATE_PART('year', ${start}))::int`;
            default:
                return (0, utils_1.assertUnreachable)(unit, `Unknown DATE_DIFF unit: ${unit}`);
        }
    }
    // Attach an OVER (…) clause to a pre-built function-call string. Lets
    // callers that need per-function argument transforms (e.g. AVG's
    // precision-preserving cast on Redshift) build the call via their own
    // emitter and still share the PARTITION BY / ORDER BY / frame plumbing.
    //
    // When the formula has no explicit ORDER BY in its OVER clause we fall
    // back to `options.defaultOrderBy` (if set). That option carries the
    // containing query's visual sort order, so `LAG(x)` without an explicit
    // ORDER BY picks the row the user sees immediately above the current
    // one in the rendered table. It also makes BigQuery and Snowflake
    // accept `LAG`/`LEAD`/`ROW_NUMBER`/... which reject an OVER clause with
    // no ORDER BY. An explicit ORDER BY in the formula always wins.
    appendOverClause(funcCall, node, frameClause) {
        const overParts = [];
        const wc = node.windowClause;
        if (wc?.partitionBy) {
            overParts.push(`PARTITION BY ${this.generate(wc.partitionBy)}`);
        }
        if (wc?.orderBy) {
            const dir = wc.orderBy.direction ? ` ${wc.orderBy.direction}` : '';
            overParts.push(`ORDER BY ${this.generate(wc.orderBy.column)}${dir}`);
        }
        else {
            const defaultOrder = this.options.defaultOrderBy;
            if (defaultOrder && defaultOrder.length > 0) {
                const cols = defaultOrder
                    .map((entry) => {
                    const dir = entry.direction
                        ? ` ${entry.direction}`
                        : '';
                    return `${this.quoteIdentifier(entry.column)}${dir}`;
                })
                    .join(', ');
                overParts.push(`ORDER BY ${cols}`);
            }
        }
        const framePart = frameClause ? ` ${frameClause}` : '';
        return `${funcCall} OVER (${overParts.join(' ')}${framePart})`;
    }
    generateWindowFunction(sqlFunc, funcArgs, node, frameClause) {
        const funcCall = funcArgs.length > 0
            ? `${sqlFunc}(${funcArgs.join(', ')})`
            : `${sqlFunc}()`;
        return this.appendOverClause(funcCall, node, frameClause);
    }
}
exports.SqlGenerator = SqlGenerator;
//# sourceMappingURL=generator.js.map